// features/schedule/components/detail/ScheduleEkipAssign.tsx
//
// Ekip block of the schedule detail panel: three role columns, one chip per
// assigned member showing where they stand on the shift, and — when the viewer
// is on the crew themselves — the accept / finish buttons.

import { useState } from "react";
import { toast } from "sonner";
import { StaffAvatar } from "@/components/StaffAvatar";
import type { Order } from "@/features/orders/api";
import { useAuthV2 as useAuth } from "@/lib/authv2/store";
import { EkipMemberList, initialMembers } from "../EkipMemberList";
import { makeupCrewLockReason, retouchCrewLockReason } from "@/features/orders/makeupCrewLock";
import { useEkipAssign, type EkipMember } from "../../useEkipAssign";
import { removalContext, useCrewRemoval } from "../../useCrewRemoval";
import { canAssignPhoto, canAssignMakeup, canAssignRetouch } from "../../permissions";
import { ekipConfirmState, useEkipConfirms, type EkipConfirmState } from "../../api";
import { ekipProxyStamp, useAcceptShiftOnBehalf } from "../../acceptOnBehalf";
import { AcceptOnBehalfDialog } from "../AcceptOnBehalfDialog";
import { usePermission } from "@/lib/authv2/permissions";
import { ACTION } from "@/lib/auth/actions";
import { useAcceptShift, useCompleteShift } from "@/features/ekip-schedule/api";
import { canMakeupUploadBeforeShoot, isShiftOpen } from "@/features/ekip-schedule/shiftWindow";
import { PhotoConfirmButton } from "@/features/ekip-schedule/PhotoConfirmButton";
import { BeforeAfterBlock } from "@/features/proofs/BeforeAfterBlock";
import { MakeupEarlyHint } from "@/features/proofs/MakeupEarlyHint";
import { useOrderProofs } from "@/features/proofs/api";
import { hasMakeupPair } from "@/features/proofs/utils";
import { useCrewAcceptOnAssign } from "@/features/products/catalogApi";
import { MakeupSplitAssign } from "./MakeupSplitAssign";
import { EkipCrewNudge } from "@/features/orders/components/EkipCrewNudge";

const STATE_STYLE: Record<EkipConfirmState, { label: string; bg: string; color: string }> = {
  confirmed: {
    label: "✅ Đã xác nhận chụp",
    bg: "var(--color-success-bg)",
    color: "var(--color-success-text)",
  },
  accepted: {
    label: "👍 Đã nhận ca",
    bg: "var(--color-info-bg)",
    color: "var(--color-info-text)",
  },
  pending: {
    label: "⏳ Chờ nhận ca",
    bg: "var(--color-warning-bg)",
    color: "var(--color-warning-text)",
  },
};

export function ScheduleEkipAssign({ order }: { order: Order }) {
  const me = useAuth((s) => s.currentStaff);
  const { addMember, isPending: ekipPending } = useEkipAssign();
  // Gỡ thợ chụp / trang điểm hỏi lý do trước (QĐ 0424).
  const { askRemove, dialog: removeDialog } = useCrewRemoval(order);
  const confirmsQ = useEkipConfirms(order.id);
  // Ảnh thẻ (QĐ 0234): được xếp vào ca là đã nhận ca — không chip "Chờ nhận
  // ca", không nút Nhận ca.
  const acceptOnAssign = useCrewAcceptOnAssign()(order.category);

  const photographers = initialMembers(order.photographers);
  const makeups = initialMembers(order.makeups);
  const retouchers = initialMembers(order.retouchers);

  // Only photo + makeup take shifts; retouch works after the shoot and has no
  // accept step, so it gets no status chip.
  const shiftCrew: { member: EkipMember; icon: string; field: "photographer" | "makeup" }[] = [
    ...photographers.map((m) => ({ member: m, icon: "📸", field: "photographer" as const })),
    ...makeups.map((m) => ({ member: m, icon: "💄", field: "makeup" as const })),
  ];
  // Nhận ca hộ (QĐ 0439): chưa nhận ca thì ca đó không có lương.
  const canProxy = usePermission(ACTION.EkipAcceptOnBehalf);

  const onCrew = shiftCrew.some((c) => c.member.id === me?.id);

  return (
    <section
      className="space-y-3 rounded-lg border p-3.5"
      style={{ background: "var(--color-info-bg)", borderColor: "var(--color-info)" }}
      aria-label="Phân công Ekip"
    >
      <div className="text-xs font-bold">🎬 Phân công Ekip</div>
      {removeDialog}

      <div className="grid gap-4 md:grid-cols-3">
        <EkipMemberList
          label="📸 Thợ chụp"
          members={photographers}
          department={["photo_idphoto", "photo_profile"]}
          branch={order.branch}
          brand={order.brandCode}
          canEdit={canAssignPhoto(me)}
          disabled={ekipPending}
          orderId={order.id}
          onAdd={(person, extra) => addMember(order.id, "photographer", person, extra)}
            onRemove={(person) => askRemove("photographer", person)}
        />
        <EkipMemberList
          label="💄 Make up"
          members={makeups}
          department="makeup"
          lockedReason={makeupCrewLockReason(order)}
          branch={order.branch}
          brand={order.brandCode}
          canEdit={canAssignMakeup(me)}
          disabled={ekipPending}
          orderId={order.id}
          onAdd={(person, extra) => addMember(order.id, "makeup", person, extra)}
            onRemove={(person) => askRemove("makeup", person)}
        />
        <EkipMemberList
          label="🖥️ Retoucher"
          members={retouchers}
          department="retouch"
          lockedReason={retouchCrewLockReason(order)}
          branch={order.branch}
          brand={order.brandCode}
          canEdit={canAssignRetouch(me)}
          disabled={ekipPending}
          orderId={order.id}
          onAdd={(person, extra) => addMember(order.id, "retouch", person, extra)}
            onRemove={(person) => askRemove("retouch", person)}
        />
      </div>

      {/* Chia việc nữ / nam / bé cho từng thợ trang điểm (QĐ 0287). Ai gán
          được thợ trang điểm thì chia được việc — cùng cổng với ô gán ở trên. */}
      {/* Nhắc ghi đủ ê-kíp (chấm QC bản 3, 4a) — chỉ nhắc, không chặn. */}
      <EkipCrewNudge order={order} />
      {makeups.length > 0 && <MakeupSplitAssign orderId={order.id} canEdit={canAssignMakeup(me)} />}

      {shiftCrew.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="ekip-confirm-chips">
          {shiftCrew.map((c) => {
            const { member, icon } = c;
            const state = ekipConfirmState(confirmsQ.data, member.id, acceptOnAssign);
            const style = STATE_STYLE[state];
            const proxied = state === "accepted" ? ekipProxyStamp(confirmsQ.data, member.id) : null;
            return (
              <span key={`${icon}-${member.id}`} className="inline-flex flex-wrap items-center gap-1.5">
                <span
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium"
                  style={{ background: style.bg, color: style.color }}
                  title={proxied ? `${proxied.by} nhận hộ: ${proxied.reason}` : undefined}
                >
                  {icon}
                  <StaffAvatar staffId={member.id} name={member.name || member.id} sizePx={22} />
                  {member.name || member.id}: {style.label}
                  {proxied ? ` (${proxied.by} nhận hộ)` : null}
                </span>
                {/* Chỉ khi sổ nhận ca đã tải: sổ chưa về thì mọi người đều
                    trông như "chờ nhận ca". */}
                {state === "pending" && canProxy && confirmsQ.data !== undefined ? (
                  <ProxyAcceptButton order={order} member={member} field={c.field} />
                ) : null}
              </span>
            );
          })}
        </div>
      )}

      {onCrew && (
        <MyShiftActions
          order={order}
          state={ekipConfirmState(confirmsQ.data, me?.id, acceptOnAssign)}
          isPhoto={photographers.some((m) => m.id === me?.id)}
          myStaffId={me?.id ?? ""}
          acceptOnAssign={acceptOnAssign}
          onChanged={() => void confirmsQ.refetch()}
        />
      )}
    </section>
  );
}

/**
 * The viewer's own controls on a shift they are assigned to. Accept first; the
 * finish button only appears once the order is actually shooting, matching the
 * BE gate on POST /ekip/confirms/:id/complete.
 *
 * Makeup còn phải nộp ảnh trước/sau trang điểm trước khi hoàn tất — giống hệt
 * thẻ ca trên Dashboard. Server chặn lần nữa (errs.ProofRequiredMakeup); ở đây
 * hiện sẵn hai ô chụp để không ai bấm rồi mới biết mình thiếu ảnh.
 */
function MyShiftActions({
  order,
  state,
  isPhoto,
  myStaffId,
  acceptOnAssign,
  onChanged,
}: {
  order: Order;
  state: EkipConfirmState;
  isPhoto: boolean;
  myStaffId: string;
  acceptOnAssign: boolean;
  onChanged: () => void;
}) {
  const acceptShift = useAcceptShift();
  const completeShift = useCompleteShift();
  // Cửa sớm (QĐ 0261): dòng tự nhận ca, đơn còn "Đã đặt" mà đã tới ngày chụp.
  const makeupEarly =
    !isPhoto &&
    state === "accepted" &&
    canMakeupUploadBeforeShoot(order.status, order.shootDate, acceptOnAssign);
  // Chỉ hỏi sổ bằng chứng khi đúng là ca makeup của mình đang mở — tránh
  // gọi thừa trên mọi panel khác.
  const { data: proofs = [] } = useOrderProofs(
    !isPhoto && state === "accepted" && (isShiftOpen(order.status) || makeupEarly)
      ? order.id
      : null,
  );
  const pairDone = hasMakeupPair(proofs, myStaffId);

  const onAccept = async () => {
    try {
      await acceptShift.mutateAsync(order.id);
      onChanged();
      toast.success("Đã nhận ca");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Nhận ca thất bại");
    }
  };

  const onComplete = async () => {
    try {
      await completeShift.mutateAsync(order.id);
      onChanged();
      toast.success("Đã xác nhận hoàn tất ca");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Hoàn tất ca thất bại");
    }
  };

  if (state === "pending") {
    return (
      <div
        className="rounded-md border-2 p-3"
        style={{ background: "var(--color-warning-bg)", borderColor: "var(--color-warning)" }}
      >
        <div className="mb-2 text-xs font-bold" style={{ color: "var(--color-warning-text)" }}>
          ⚡ Bạn được phân công {isPhoto ? "Photo" : "Makeup"} cho ca này
        </div>
        <button
          type="button"
          onClick={onAccept}
          disabled={acceptShift.isPending}
          className="rounded-md px-6 py-2 text-sm font-bold text-white disabled:opacity-60"
          style={{ background: "var(--color-warning-text)" }}
        >
          {acceptShift.isPending ? "Đang xử lý…" : "✓ Nhận ca"}
        </button>
      </div>
    );
  }

  // Cửa sớm (QĐ 0261) đi CÙNG khối với ca đang chụp: nộp đủ ảnh rồi bấm
  // "Hoàn tất makeup" là máy chủ chuyển đơn sang Đang chụp.
  if ((state === "accepted" && isShiftOpen(order.status)) || makeupEarly) {
    return (
      <div
        className="rounded-md border p-3"
        style={{ background: "var(--color-success-bg)", borderColor: "var(--color-success)" }}
      >
        {!isPhoto && (
          <>
            <BeforeAfterBlock orderId={order.id} myStaffId={myStaffId} />
            {!pairDone && (
              <div className="mt-1.5 text-[10px] font-semibold text-[var(--color-warning-text)]">
                Chụp đủ ảnh trước và sau trang điểm mới hoàn tất được ca
              </div>
            )}
            {makeupEarly && <MakeupEarlyHint />}
          </>
        )}
        <div className={isPhoto ? "" : "mt-2 border-t border-[var(--color-success)]/30 pt-2"}>
          <PhotoConfirmButton
            isPhoto={isPhoto}
            disabled={completeShift.isPending || (!isPhoto && !pairDone)}
            onConfirm={onComplete}
          />
        </div>
      </div>
    );
  }

  // Xong phần mình nhưng ca vẫn đang chụp = còn người trong ekip chưa xác nhận.
  if (state === "confirmed" && order.status === "shooting") {
    return (
      <div
        className="rounded-md border p-3 text-xs font-semibold"
        style={{
          background: "var(--color-warning-bg)",
          borderColor: "var(--color-warning)",
          color: "var(--color-warning-text)",
        }}
      >
        Đã xác nhận xong phần {isPhoto ? "chụp" : "trang điểm"} — chờ thành viên còn lại của ekip
        xác nhận rồi đơn mới sang sửa hình
      </div>
    );
  }

  return null;
}

/**
 * Nút "Nhận hộ" cạnh chip "⏳ Chờ nhận ca" (QĐ 0439) + hộp ghi lý do. Tách
 * riêng để chỉ người có quyền nhận hộ mới dựng lệnh ghi.
 */
function ProxyAcceptButton({
  order,
  member,
  field,
}: {
  order: Order;
  member: EkipMember;
  field: "photographer" | "makeup";
}) {
  const proxy = useAcceptShiftOnBehalf(order.id);
  const [open, setOpen] = useState(false);
  const name = member.name || member.id;
  return (
    <>
      <button
        type="button"
        className="min-h-[var(--touch-min)] rounded-md border border-[var(--color-warning)] px-2 py-1 text-xs font-medium text-[var(--color-warning-text)] hover:bg-[var(--color-warning-bg)] md:min-h-0"
        aria-label={`Nhận ca hộ ${name}`}
        onClick={() => setOpen(true)}
      >
        Nhận hộ
      </button>
      <AcceptOnBehalfDialog
        personName={open ? name : null}
        context={removalContext(order, field)}
        pending={proxy.isPending}
        onOpenChange={setOpen}
        onConfirm={(reason) =>
          proxy.mutate(
            { staffId: member.id, reason },
            {
              onSuccess: () => {
                toast.success(`Đã nhận ca hộ ${name}`);
                setOpen(false);
              },
              onError: (e) => toast.error(e instanceof Error ? e.message : "Không nhận ca hộ được"),
            },
          )
        }
      />
    </>
  );
}
